import { useState, useEffect, useRef, lazy, Suspense } from "react";
import { Routes, Route, Link, Navigate, useLocation, useNavigate } from "react-router-dom";
import { ErrorBoundary } from "./components/ErrorBoundary";
import {
  LayoutDashboard,
  Activity,
  AlertTriangle,
  Bot,
  ListTodo,
  Workflow,
  Settings,
  ShoppingBag,
  Shield,
  LogOut,
  MessageSquareText,
  FolderOpen,
  GitBranch,
  CheckCircle2,
  Wrench,
  TerminalSquare,
  Database,
  Key,
  Palette,
  Building2,
  Bug,
  Server,
  Lock,
  ScrollText,
  ChevronLeft,
  ChevronRight,
  Gauge,
  Users,
  Rocket,
  WalletCards,
  type LucideIcon,
} from "lucide-react";
import {
  api,
  getSupportSession,
  onSupportSessionChanged,
  setSessionUser,
  setSupportSession,
  setToken,
  type TenantBrandingEntry,
  type AuthenticatedUser,
  type PlatformSupportSessionEntry,
  type TenantOnboardingCompletionSourceEntry,
} from "./api/client";
import { getOnboardingCompleted, setOnboardingCompleted } from "./onboarding";
import { consumeSignupIntent } from "./signup-intent";
import { BRAND_DISPLAY_NAME, BRAND_TAGLINE } from "./config/brand";
import { applyTenantBranding, TENANT_BRANDING_CHANGED_EVENT } from "./tenant-branding";

const DashboardPage = lazy(() =>
  import("./pages/Dashboard").then((module) => ({ default: module.DashboardPage })),
);
const AgentsPage = lazy(() =>
  import("./pages/Agents").then((module) => ({ default: module.AgentsPage })),
);
const TasksPage = lazy(() =>
  import("./pages/TasksRunsPage").then((module) => ({ default: module.TasksPage })),
);
const RunChatPage = lazy(() =>
  import("./pages/RunChatPage").then((module) => ({ default: module.RunChatPage })),
);
const ThreadsPage = lazy(() =>
  import("./pages/ThreadsPage").then((module) => ({ default: module.ThreadsPage })),
);
const ApprovalsPage = lazy(() =>
  import("./pages/ApprovalsPage").then((module) => ({ default: module.ApprovalsPage })),
);
const OperationsPage = lazy(() =>
  import("./pages/OperationsPage").then((module) => ({ default: module.OperationsPage })),
);
const BuilderPage = lazy(() =>
  import("./pages/BuilderFlowPage").then((module) => ({ default: module.BuilderPage })),
);
const AgentSimulationStudioPage = lazy(() =>
  import("./pages/AgentSimulationStudioPage").then((module) => ({
    default: module.AgentSimulationStudioPage,
  })),
);
const ProductPositionPage = lazy(() =>
  import("./pages/ProductPositionPage").then((module) => ({ default: module.ProductPositionPage })),
);
const ProductSurfacePage = lazy(() =>
  import("./pages/ProductSurfacePage").then((module) => ({ default: module.ProductSurfacePage })),
);
const EvaluationLabPage = lazy(() =>
  import("./pages/EvaluationLabPage").then((module) => ({ default: module.EvaluationLabPage })),
);
const PromptManagementPage = lazy(() =>
  import("./pages/PromptManagementPage").then((module) => ({ default: module.PromptManagementPage })),
);
const BusinessRoiCockpitPage = lazy(() =>
  import("./pages/BusinessRoiCockpitPage").then((module) => ({ default: module.BusinessRoiCockpitPage })),
);
const AgentReleaseManagementPage = lazy(() =>
  import("./pages/AgentReleaseManagementPage").then((module) => ({ default: module.AgentReleaseManagementPage })),
);
const MarketplacePage = lazy(() =>
  import("./pages/Marketplace").then((module) => ({ default: module.MarketplacePage })),
);
const MarketplaceCatalogDetailPage = lazy(() =>
  import("./pages/MarketplaceCatalogDetailPage").then((module) => ({
    default: module.MarketplaceCatalogDetailPage,
  })),
);
const PackMarketplaceRegistryPage = lazy(() =>
  import("./pages/PackMarketplaceRegistryPage").then((module) => ({
    default: module.PackMarketplaceRegistryPage,
  })),
);
const SettingsPage = lazy(() =>
  import("./pages/Settings").then((module) => ({ default: module.SettingsPage })),
);
const LoginPage = lazy(() =>
  import("./pages/Login").then((module) => ({ default: module.LoginPage })),
);
const PricingPage = lazy(() =>
  import("./pages/PricingPage").then((module) => ({ default: module.PricingPage })),
);
const MarketingPage = lazy(() =>
  import("./pages/MarketingPage").then((module) => ({ default: module.MarketingPage })),
);
const PublicMarketplacePage = lazy(() =>
  import("./pages/PublicMarketplacePage").then((module) => ({
    default: module.PublicMarketplacePage,
  })),
);
const BuildYourOwnConnectorPage = lazy(() =>
  import("./pages/BuildYourOwnConnectorPage").then((module) => ({
    default: module.BuildYourOwnConnectorPage,
  })),
);
const TemplateWizardPage = lazy(() =>
  import("./pages/TemplateWizardPage").then((module) => ({ default: module.TemplateWizardPage })),
);
const OnboardingPage = lazy(() =>
  import("./pages/OnboardingPage").then((module) => ({ default: module.OnboardingPage })),
);
const QuickAgentWizardPage = lazy(() =>
  import("./pages/QuickAgentWizardPage").then((module) => ({ default: module.QuickAgentWizardPage })),
);
const FilesPage = lazy(() =>
  import("./pages/FilesPage").then((module) => ({ default: module.FilesPage })),
);
const AgentDetailPage = lazy(() =>
  import("./pages/AgentDetailPage").then((module) => ({ default: module.AgentDetailPage })),
);
const SessionsPage = lazy(() =>
  import("./pages/SessionsPage").then((module) => ({ default: module.SessionsPage })),
);
const DebuggerPage = lazy(() =>
  import("./pages/DebuggerPage").then((module) => ({ default: module.DebuggerPage })),
);
const ObservabilityPage = lazy(() =>
  import("./pages/ObservabilityPage").then((module) => ({ default: module.ObservabilityPage })),
);
const ConnectorsPage = lazy(() =>
  import("./pages/ConnectorsPage").then((module) => ({ default: module.ConnectorsPage })),
);
const OutboundMcpPage = lazy(() =>
  import("./pages/OutboundMcpPage").then((module) => ({ default: module.OutboundMcpPage })),
);
const AdminDlpPolicyPage = lazy(() =>
  import("./pages/AdminDlpPolicyPage").then((module) => ({ default: module.AdminDlpPolicyPage })),
);
const AdminDlpActivityPage = lazy(() =>
  import("./pages/AdminDlpActivityPage").then((module) => ({
    default: module.AdminDlpActivityPage,
  })),
);
const PlatformAdminFleetOverviewPage = lazy(() =>
  import("./pages/PlatformAdminFleetOverviewPage").then((module) => ({
    default: module.PlatformAdminFleetOverviewPage,
  })),
);
const PlatformAdminWorkspacesPage = lazy(() =>
  import("./pages/PlatformAdminWorkspacesPage").then((module) => ({
    default: module.PlatformAdminWorkspacesPage,
  })),
);
const PlatformAdminWorkspaceDetailPage = lazy(() =>
  import("./pages/PlatformAdminWorkspaceDetailPage").then((module) => ({
    default: module.PlatformAdminWorkspaceDetailPage,
  })),
);
const PlatformAdminUsersPage = lazy(() =>
  import("./pages/PlatformAdminUsersPage").then((module) => ({
    default: module.PlatformAdminUsersPage,
  })),
);
const PlatformAdminPlansPage = lazy(() =>
  import("./pages/PlatformAdminPlansPage").then((module) => ({
    default: module.PlatformAdminPlansPage,
  })),
);

type SidebarItem = {
  to: string;
  icon: LucideIcon;
  label: string;
};

type SidebarSection = {
  label: string;
  items: SidebarItem[];
};

const BASE_NAV_SECTIONS: SidebarSection[] = [
  {
    label: "Build",
    items: [
      { to: "/dashboard", icon: LayoutDashboard, label: "Control Tower" },
      { to: "/agents", icon: Bot, label: "Agents" },
      { to: "/builder", icon: Workflow, label: "Builder" },
      { to: "/simulation-studio", icon: Shield, label: "Simulation" },
      { to: "/evaluations", icon: Activity, label: "Eval Lab" },
      { to: "/prompts", icon: MessageSquareText, label: "Prompts" },
      { to: "/business-roi", icon: WalletCards, label: "Business ROI" },
      { to: "/agent-releases", icon: Rocket, label: "Releases" },
      { to: "/marketplace", icon: ShoppingBag, label: "Marketplace" },
      { to: "/marketplace/registry", icon: ShoppingBag, label: "Pack Registry" },
      { to: "/chat", icon: MessageSquareText, label: "Chat" },
    ],
  },
  {
    label: "Operate",
    items: [
      { to: "/tasks", icon: ListTodo, label: "Runs" },
      { to: "/threads", icon: GitBranch, label: "Threads" },
      { to: "/approvals", icon: CheckCircle2, label: "Approvals" },
      { to: "/dashboard#alerts", icon: AlertTriangle, label: "Alerts" },
      { to: "/files", icon: FolderOpen, label: "Files" },
    ],
  },
  {
    label: "Platform",
    items: [
      { to: "/operations", icon: TerminalSquare, label: "Operations" },
      { to: "/observability", icon: Activity, label: "Observability" },
      { to: "/connectors", icon: Wrench, label: "Connectors" },
      { to: "/sessions", icon: Database, label: "Sessions" },
      { to: "/outbound-mcp", icon: Server, label: "Outbound MCP" },
      { to: "/admin/dlp", icon: Lock, label: "DLP Policy" },
      { to: "/admin/dlp-activity", icon: ScrollText, label: "DLP Activity" },
      { to: "/debugger", icon: Bug, label: "Debugger" },
    ],
  },
];

const FLEET_NAV_SECTION: SidebarSection = {
  label: "Fleet",
  items: [
    { to: "/platform/fleet", icon: Gauge, label: "Fleet Overview" },
    { to: "/platform/workspaces", icon: Building2, label: "Fleet Workspaces" },
    { to: "/platform/users", icon: Users, label: "Fleet Users" },
    { to: "/platform/plans", icon: WalletCards, label: "Plan Catalog" },
  ],
};

const INTERNAL_NAV_SECTION: SidebarSection = {
  label: "Internal",
  items: [
    { to: "/product-position", icon: ScrollText, label: "Product Workbench" },
  ],
};

const SETTINGS_NAV_SECTION: SidebarSection = {
  label: "Settings",
  items: [
    { to: "/settings", icon: Settings, label: "General" },
    { to: "/settings/users", icon: Users, label: "Users" },
    { to: "/settings/api", icon: Key, label: "API keys" },
    { to: "/settings/runners", icon: Server, label: "Local runners" },
    { to: "/settings/theme", icon: Palette, label: "Theme" },
    { to: "/settings/features", icon: Shield, label: "Features" },
    { to: "/settings/enterprise", icon: Shield, label: "Enterprise" },
  ],
};

const SETTINGS_ITEM: SidebarItem = { to: "/settings", icon: Settings, label: "Settings" };
const ADMIN_LINKS_ITEM: SidebarItem = { to: "/dashboard", icon: ChevronLeft, label: "Back to admin" };

function isSettingsPath(pathname: string): boolean {
  return pathname === "/settings" || pathname.startsWith("/settings/");
}

export function isSidebarItemActive(pathname: string, itemTo: string, currentHash = ""): boolean {
  const [targetPath, targetHash] = itemTo.split("#");
  if (targetHash) {
    return pathname === targetPath && currentHash === `#${targetHash}`;
  }
  if (currentHash && pathname === targetPath) {
    return false;
  }
  if (itemTo === "/marketplace") {
    return pathname === "/marketplace" || pathname.startsWith("/marketplace/catalog/");
  }
  if (itemTo === "/marketplace/registry") {
    return pathname === "/marketplace/registry";
  }
  if (itemTo === "/settings") {
    return pathname === "/settings" && !currentHash;
  }
  return pathname === itemTo || pathname.startsWith(`${itemTo}/`);
}

function canManagePlatform(role: string): boolean {
  return role === "platform:ops" || role === "system";
}

export function canAccessProductWorkbench(tenantId: string, role: string): boolean {
  return tenantId === "system" || role === "system";
}

export function buildSidebarSections(role: string, tenantId: string, pathname = ""): SidebarSection[] {
  if (isSettingsPath(pathname)) {
    return [SETTINGS_NAV_SECTION];
  }

  const sections = canManagePlatform(role)
    ? [BASE_NAV_SECTIONS[0]!, BASE_NAV_SECTIONS[1]!, FLEET_NAV_SECTION, BASE_NAV_SECTIONS[2]!]
    : BASE_NAV_SECTIONS;
  return canAccessProductWorkbench(tenantId, role) ? [...sections, INTERNAL_NAV_SECTION] : sections;
}

function SidebarLink({
  item,
  collapsed,
  surface = "primary",
}: {
  item: SidebarItem;
  collapsed: boolean;
  surface?: "primary" | "secondary";
}) {
  const { to, icon: Icon, label } = item;
  const location = useLocation();
  const isActive = isSidebarItemActive(location.pathname, to, location.hash);

  return (
    <Link
      to={to}
      aria-label={label}
      aria-current={isActive ? "page" : undefined}
      title={collapsed ? label : undefined}
      className={
        `group relative flex overflow-hidden rounded-xl border text-sm font-medium transition-all duration-150 ${
          collapsed ? "justify-center px-2 py-2.5" : "items-center gap-3 px-3 py-2.5"
        } ${
          isActive
            ? "border-brand-100 bg-brand-50/95 text-brand-700 shadow-sm"
            : surface === "secondary"
              ? "border-gray-200 bg-gray-50/80 text-gray-600 hover:border-gray-300 hover:bg-white hover:text-gray-900"
              : "border-transparent text-gray-600 hover:border-gray-200 hover:bg-gray-50 hover:text-gray-900"
        }`
      }
    >
      <span
        aria-hidden="true"
        className={`absolute inset-y-2 left-0 w-1 rounded-r-full bg-brand-600 transition-opacity ${
          isActive ? "opacity-100" : "opacity-0"
        }`}
      />
      <Icon className="h-4.5 w-4.5 shrink-0" />
      {!collapsed ? <span className="truncate">{label}</span> : null}
    </Link>
  );
}

function Sidebar({
  userName,
  tenantId,
  role,
  branding,
  collapsed,
  onToggle,
  onLogout,
}: {
  userName: string;
  tenantId: string;
  role: string;
  branding: TenantBrandingEntry | null;
  collapsed: boolean;
  onToggle: () => void;
  onLogout: () => void;
}) {
  const location = useLocation();
  const settingsRoute = isSettingsPath(location.pathname);
  const navSections = buildSidebarSections(role, tenantId, location.pathname);
  const displayName = branding?.appName?.trim() || BRAND_DISPLAY_NAME;
  const [brandWordA, ...remainingBrandWords] = displayName.split(/\s+/g);
  const brandWordB = remainingBrandWords.join(" ");
  const defaultBrandMarkSrc = `${import.meta.env.BASE_URL}logo-mark.svg`;
  const brandMarkSrc = branding?.logoUrl?.trim() || defaultBrandMarkSrc;

  return (
    <aside
      className={`fixed bottom-0 left-0 top-0 z-10 flex flex-col overflow-hidden border-r border-gray-200 bg-white transition-[width] duration-200 ease-out ${
        collapsed ? "w-[4.5rem]" : "w-56"
      }`}
    >
      <div
        className={`border-b border-gray-200 ${collapsed ? "flex h-14 items-center px-2" : "relative px-4 py-3"}`}
      >
        <div className={`flex min-w-0 items-center ${collapsed ? "w-full gap-1" : "gap-2.5"}`}>
          <div className={`flex min-w-0 items-center ${collapsed ? "justify-center" : "flex-1 gap-2.5"}`}>
            <img
              src={brandMarkSrc}
              alt=""
              width={36}
              height={36}
              onError={(event) => {
                if (event.currentTarget.getAttribute("src") !== defaultBrandMarkSrc) {
                  event.currentTarget.src = defaultBrandMarkSrc;
                }
              }}
              className="h-9 w-9 shrink-0 rounded-lg shadow-sm ring-1 ring-gray-200/90"
            />
            {!collapsed ? (
              <div className="min-w-0 leading-tight">
                <div className="whitespace-nowrap text-[15px] font-semibold text-brand-700">
                  {brandWordA}{brandWordB ? <span className="text-brand-600"> {brandWordB}</span> : null}
                </div>
              </div>
            ) : null}
          </div>
          <button
            type="button"
            onClick={onToggle}
            className={`${collapsed ? "ml-auto" : "absolute right-2 top-3"} rounded-lg p-2 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600`}
            aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
            title={collapsed ? "Expand sidebar" : "Collapse sidebar"}
          >
            {collapsed ? <ChevronRight className="h-4 w-4" /> : <ChevronLeft className="h-4 w-4" />}
          </button>
        </div>
        {!collapsed ? (
          <p className="mt-2 whitespace-nowrap text-[9.5px] font-medium leading-none text-gray-500">{BRAND_TAGLINE}</p>
        ) : null}
      </div>
      <div className="relative min-h-0 flex-1">
        <div className="pointer-events-none absolute inset-x-0 top-0 z-10 h-5 bg-gradient-to-b from-white via-white/90 to-transparent" />
        <div className="pointer-events-none absolute inset-x-0 bottom-0 z-10 h-5 bg-gradient-to-t from-white via-white/90 to-transparent" />
        <nav
          className={`sidebar-scrollbar h-full overflow-y-auto py-3 ${collapsed ? "px-2" : "px-3 pr-2"}`}
          aria-label="Primary"
        >
          <div className="space-y-4">
            {settingsRoute ? (
              <section className="space-y-2 border-b border-gray-200 pb-3">
                <SidebarLink item={ADMIN_LINKS_ITEM} collapsed={collapsed} surface="secondary" />
              </section>
            ) : null}
            {navSections.map((section, index) => (
              <section key={section.label} className="space-y-2">
                {collapsed ? (
                  index > 0 ? (
                    <div className="mx-2 h-px bg-gray-200" aria-hidden="true" />
                  ) : null
                ) : (
                  <p className="px-3 text-[11px] font-semibold uppercase tracking-[0.18em] text-gray-400">
                    {section.label}
                  </p>
                )}
                <div className="space-y-1">
                  {section.items.map((item) => (
                    <SidebarLink key={item.to} item={item} collapsed={collapsed} />
                  ))}
                </div>
              </section>
            ))}
          </div>
        </nav>
      </div>
      <div className={`border-t border-gray-200 ${collapsed ? "p-3" : "p-4"}`}>
        <div className="space-y-3">
          {!settingsRoute ? <SidebarLink item={SETTINGS_ITEM} collapsed={collapsed} surface="secondary" /> : null}
          <div
            className={`rounded-xl border border-gray-200 bg-gray-50/80 ${collapsed ? "p-2.5" : "p-3"}`}
          >
            <div
              className={`flex gap-2 ${collapsed ? "flex-col items-center" : "items-center justify-between"}`}
            >
              <div
                className={`flex ${collapsed ? "justify-center" : "min-w-0 items-center gap-2"}`}
                title={collapsed ? `${userName} - ${tenantId} | ${role}` : undefined}
              >
                <div className="flex h-7 w-7 items-center justify-center rounded-full bg-brand-100">
                  <span className="text-xs font-medium text-brand-700">
                    {userName.charAt(0).toUpperCase()}
                  </span>
                </div>
                {!collapsed ? (
                  <div className="min-w-0">
                    <p className="max-w-[108px] truncate text-xs font-medium text-gray-700">
                      {userName}
                    </p>
                    <p className="max-w-[108px] truncate text-[11px] text-gray-400">
                      {tenantId} | {role}
                    </p>
                  </div>
                ) : null}
              </div>
              <button
                type="button"
                onClick={onLogout}
                className="rounded-lg p-1.5 text-gray-400 transition-colors hover:bg-white hover:text-gray-600"
                title="Sign out"
                aria-label="Sign out"
              >
                <LogOut className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </aside>
  );
}

function RouteFallback() {
  return (
    <div className="flex min-h-[60vh] items-center justify-center">
      <div className="animate-spin h-8 w-8 rounded-full border-2 border-brand-600 border-t-transparent" />
    </div>
  );
}

export function hasAuthEntrySearchParams(search: string): boolean {
  const params = new URLSearchParams(search);
  return (
    params.has("invite_token") ||
    params.has("reset_token") ||
    params.has("sso_token") ||
    params.has("sso_error") ||
    params.get("mode") === "login" ||
    params.get("mode") === "register"
  );
}

export function SupportSessionBanner({
  sessions,
  supportModeSessionId,
  currentRole,
}: {
  sessions: readonly PlatformSupportSessionEntry[];
  supportModeSessionId: string | null;
  currentRole: string;
}) {
  if (sessions.length === 0) {
    return null;
  }

  const activeSession = sessions.find((session) => session.sessionId === supportModeSessionId) ?? sessions[0]!;
  const supportModeEnabled = activeSession.sessionId === supportModeSessionId;

  return (
    <div className="mb-4 rounded-[24px] border border-amber-200 bg-[linear-gradient(135deg,_rgba(254,243,199,0.95),_rgba(255,251,235,0.98))] px-5 py-4 text-slate-900 shadow-sm">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-amber-700">Support session active</p>
          <p className="mt-2 text-sm font-semibold text-slate-950">
            {activeSession.tenantName ?? activeSession.tenantId} is under time-bounded operator support until {new Intl.DateTimeFormat(undefined, {
              dateStyle: "medium",
              timeStyle: "short",
            }).format(new Date(activeSession.expiresAt))}.
          </p>
          <p className="mt-2 text-sm text-slate-700">{activeSession.reason}</p>
        </div>
        {supportModeEnabled && canManagePlatform(currentRole) ? (
          <button
            type="button"
            onClick={() => setSupportSession(null)}
            className="rounded-full border border-amber-300 bg-white/90 px-4 py-2 text-sm font-medium text-amber-800 hover:bg-white"
          >
            Exit support mode
          </button>
        ) : null}
      </div>
    </div>
  );
}

export function App() {
  const location = useLocation();
  const navigate = useNavigate();
  const [user, setUser] = useState<AuthenticatedUser | null>(null);
  const [checking, setChecking] = useState(true);
  const [checkingOnboarding, setCheckingOnboarding] = useState(false);
  const [needsOnboarding, setNeedsOnboarding] = useState(false);
  const [branding, setBranding] = useState<TenantBrandingEntry | null>(null);
  const [supportSessions, setSupportSessions] = useState<PlatformSupportSessionEntry[]>([]);
  const [supportModeSessionId, setSupportModeSessionId] = useState<string | null>(() => getSupportSession()?.sessionId ?? null);
  const isBuilderRoute = location.pathname.startsWith("/builder");
  const hasAuthEntryQuery = hasAuthEntrySearchParams(location.search);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(isBuilderRoute);
  const previousPathRef = useRef(location.pathname);

  useEffect(() => onSupportSessionChanged(() => {
    setSupportModeSessionId(getSupportSession()?.sessionId ?? null);
  }), []);

  useEffect(() => {
    const handleBrandingChanged = (event: Event) => {
      setBranding((event as CustomEvent<TenantBrandingEntry>).detail);
    };

    window.addEventListener(TENANT_BRANDING_CHANGED_EVENT, handleBrandingChanged);
    return () => window.removeEventListener(TENANT_BRANDING_CHANGED_EVENT, handleBrandingChanged);
  }, []);

  useEffect(() => {
    applyTenantBranding(branding);
  }, [branding]);

  useEffect(() => {
    // Check if there's an existing session
    api.auth
      .me()
      .then((data) => {
        if (data.user) {
          setSessionUser(data.user);
          setUser(data.user);
        }
      })
      .catch(() => {
        setToken(null);
        setSessionUser(null);
      })
      .finally(() => setChecking(false));
  }, []);

  useEffect(() => {
    if (!user) {
      setNeedsOnboarding(false);
      setCheckingOnboarding(false);
      setBranding(null);
      return;
    }

    const adminUser = user.role === "platform:admin" || user.role === "system";
    if (!adminUser) {
      setNeedsOnboarding(false);
      setCheckingOnboarding(false);
      return;
    }

    let cancelled = false;
    setCheckingOnboarding(true);

    (async () => {
      const localCompleted = getOnboardingCompleted(user.tenantId);
      let onboarding = (await api.saas.onboarding()).onboarding;
      if (cancelled) return;

      if (!onboarding.completed && localCompleted) {
        onboarding = (await api.saas.completeOnboarding({
          source: "local_migration",
          metadata: { migratedFrom: "localStorage" },
        })).onboarding;
        if (cancelled) return;
      }

      setOnboardingCompleted(user.tenantId, onboarding.completed);
      setNeedsOnboarding(!onboarding.completed);
    })()
      .catch(() => {
        if (!cancelled) {
          setNeedsOnboarding(false);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setCheckingOnboarding(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [user]);

  useEffect(() => {
    if (!user) return;

    let cancelled = false;
    api.saas.branding()
      .then((response) => {
        if (!cancelled) {
          setBranding(response.branding);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setBranding(null);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [user]);

  // After a successful sign-up that selected a plan on /pricing, start hosted checkout
  // when billing is configured. Free plans no-op, and unavailable paid plans keep the
  // user in the workspace without granting paid entitlements.
  useEffect(() => {
    if (!user) return;
    const intent = consumeSignupIntent();
    if (!intent) return;

    let cancelled = false;
    (async () => {
      try {
        const result = await api.billing.checkout(intent.planId);
        if (cancelled) return;
        const checkoutUrl = result.session.checkoutUrl;
        if (
          result.session.mode === "redirect" &&
          typeof checkoutUrl === "string" &&
          checkoutUrl.length > 0
        ) {
          window.location.assign(checkoutUrl);
        }
      } catch {
        // Checkout is best-effort; failing here should not block the user from using the
        // workspace they just created. The backend keeps paid entitlements disabled unless
        // the payment provider or an operator-approved path completes activation.
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [user]);

  const handleLogin = (userData: AuthenticatedUser, token: string) => {
    setSessionUser(userData);
    setUser(userData);
  };

  const handleOnboardingComplete = async (source: TenantOnboardingCompletionSourceEntry = "skipped") => {
    if (user) {
      try {
        await api.saas.completeOnboarding({ source });
      } catch {
        // Keep onboarding escapable if the completion acknowledgement cannot be persisted.
      }
      setOnboardingCompleted(user.tenantId, true);
    }
    setNeedsOnboarding(false);
  };

  const handleLogout = async () => {
    try {
      await api.auth.logout();
    } catch {
      // Clear local auth state even if the backend session has already expired.
    } finally {
      setToken(null);
      setSessionUser(null);
      setSupportSession(null);
      setNeedsOnboarding(false);
      setUser(null);
      navigate("/", { replace: true });
    }
  };

  useEffect(() => {
    if (!user) {
      setSupportSessions([]);
      return;
    }

    let cancelled = false;
    api.saas.activeSupportSessions()
      .then((response) => {
        if (cancelled) {
          return;
        }
        setSupportSessions(response.sessions);
        const currentSupportSession = getSupportSession();
        if (currentSupportSession && !response.sessions.some((session) => session.sessionId === currentSupportSession.sessionId && session.status === "active")) {
          setSupportSession(null);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setSupportSessions([]);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [location.pathname, supportModeSessionId, user]);

  useEffect(() => {
    const wasBuilderRoute = previousPathRef.current.startsWith("/builder");
    if (isBuilderRoute && !wasBuilderRoute) {
      setSidebarCollapsed(true);
    }
    previousPathRef.current = location.pathname;
  }, [isBuilderRoute, location.pathname]);

  if (checking || checkingOnboarding) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-2 border-brand-600 border-t-transparent rounded-full" />
      </div>
    );
  }

  if (!user) {
    return (
      <Suspense fallback={<RouteFallback />}>
        <Routes>
          <Route path="/" element={hasAuthEntryQuery ? <LoginPage onLogin={handleLogin} /> : <MarketingPage />} />
          <Route path="/pricing" element={<PricingPage />} />
          <Route path="/marketplace" element={<PublicMarketplacePage />} />
          <Route path="/login" element={<LoginPage onLogin={handleLogin} />} />
          <Route path="/reset-password" element={<LoginPage onLogin={handleLogin} />} />
          <Route
            path="/signup"
            element={<LoginPage onLogin={handleLogin} initialMode="register" />}
          />
          <Route path="*" element={<LoginPage onLogin={handleLogin} />} />
        </Routes>
      </Suspense>
    );
  }

  const shelllessOnboarding = location.pathname === "/onboarding";
  const guarded = (element: React.ReactNode) =>
    needsOnboarding ? <Navigate to="/onboarding" replace /> : element;
  const platformGuarded = (element: React.ReactNode) =>
    guarded(canManagePlatform(user.role) ? element : <Navigate to="/dashboard" replace />);
  const productWorkbenchGuarded = (element: React.ReactNode) =>
    guarded(canAccessProductWorkbench(user.tenantId, user.role) ? element : <Navigate to="/dashboard" replace />);
  const mainShellClassName = shelllessOnboarding
    ? ""
    : `${sidebarCollapsed ? "ml-[4.5rem]" : "ml-56"} min-w-0 flex-1 ${isBuilderRoute ? "p-4" : "p-6"} transition-[margin] duration-200 ease-out`;

  return (
    <div className={shelllessOnboarding ? "min-h-screen" : "min-h-screen flex"}>
      {!shelllessOnboarding ? (
        <Sidebar
          userName={user.name || user.email}
          tenantId={user.tenantId}
          role={user.role}
          branding={branding}
          collapsed={sidebarCollapsed}
          onToggle={() => setSidebarCollapsed((value) => !value)}
          onLogout={handleLogout}
        />
      ) : null}
      <main className={mainShellClassName}>
        <SupportSessionBanner
          sessions={supportSessions}
          supportModeSessionId={supportModeSessionId}
          currentRole={user.role}
        />
        <Suspense fallback={<RouteFallback />}>
          <Routes>
            <Route
              path="/"
              element={<Navigate to={needsOnboarding ? "/onboarding" : "/dashboard"} replace />}
            />
            <Route
              path="/onboarding"
              element={
                needsOnboarding ? (
                  <ErrorBoundary label="Onboarding">
                    <OnboardingPage currentUser={user} onComplete={handleOnboardingComplete} />
                  </ErrorBoundary>
                ) : (
                  <Navigate to="/dashboard" replace />
                )
              }
            />
            <Route
              path="/quick-agent"
              element={guarded(
                <ErrorBoundary label="Quick agent wizard">
                  <QuickAgentWizardPage user={user} />
                </ErrorBoundary>,
              )}
            />
            <Route
              path="/pricing"
              element={
                <ErrorBoundary label="Pricing">
                  <PricingPage />
                </ErrorBoundary>
              }
            />
            <Route path="/signup" element={<Navigate to="/dashboard" replace />} />
            <Route
              path="/dashboard"
              element={guarded(
                <ErrorBoundary label="Dashboard">
                  <DashboardPage />
                </ErrorBoundary>,
              )}
            />
            <Route
              path="/agents"
              element={guarded(
                <ErrorBoundary label="Agents">
                  <AgentsPage currentUser={user} />
                </ErrorBoundary>,
              )}
            />
            <Route
              path="/agents/:id"
              element={guarded(
                <ErrorBoundary label="Agent detail">
                  <AgentDetailPage />
                </ErrorBoundary>,
              )}
            />
            <Route
              path="/tasks"
              element={guarded(
                <ErrorBoundary label="Tasks">
                  <TasksPage />
                </ErrorBoundary>,
              )}
            />
            <Route
              path="/threads"
              element={guarded(
                <ErrorBoundary label="Threads">
                  <ThreadsPage />
                </ErrorBoundary>,
              )}
            />
            <Route
              path="/threads/:threadId"
              element={guarded(
                <ErrorBoundary label="Thread detail">
                  <ThreadsPage />
                </ErrorBoundary>,
              )}
            />
            <Route
              path="/approvals"
              element={guarded(
                <ErrorBoundary label="Approvals">
                  <ApprovalsPage />
                </ErrorBoundary>,
              )}
            />
            <Route
              path="/operations"
              element={guarded(
                <ErrorBoundary label="Operations">
                  <OperationsPage currentUser={user} />
                </ErrorBoundary>,
              )}
            />
            <Route
              path="/platform/fleet"
              element={platformGuarded(
                <ErrorBoundary label="Fleet overview">
                  <PlatformAdminFleetOverviewPage />
                </ErrorBoundary>,
              )}
            />
            <Route
              path="/platform/workspaces"
              element={platformGuarded(
                <ErrorBoundary label="Fleet workspaces">
                  <PlatformAdminWorkspacesPage />
                </ErrorBoundary>,
              )}
            />
            <Route
              path="/platform/workspaces/:tenantId"
              element={platformGuarded(
                <ErrorBoundary label="Fleet workspace detail">
                  <PlatformAdminWorkspaceDetailPage />
                </ErrorBoundary>,
              )}
            />
            <Route
              path="/platform/users"
              element={platformGuarded(
                <ErrorBoundary label="Fleet users">
                  <PlatformAdminUsersPage />
                </ErrorBoundary>,
              )}
            />
            <Route
              path="/platform/plans"
              element={platformGuarded(
                <ErrorBoundary label="Platform plans">
                  <PlatformAdminPlansPage />
                </ErrorBoundary>,
              )}
            />
            <Route
              path="/sessions"
              element={guarded(
                <ErrorBoundary label="Sessions">
                  <SessionsPage />
                </ErrorBoundary>,
              )}
            />
            <Route
              path="/connectors"
              element={guarded(
                <ErrorBoundary label="Connectors">
                  <ConnectorsPage />
                </ErrorBoundary>,
              )}
            />
            <Route
              path="/connectors/build"
              element={guarded(
                <ErrorBoundary label="Build a connector">
                  <BuildYourOwnConnectorPage />
                </ErrorBoundary>,
              )}
            />
            <Route
              path="/debugger"
              element={guarded(
                <ErrorBoundary label="Debugger">
                  <DebuggerPage />
                </ErrorBoundary>,
              )}
            />
            <Route
              path="/observability"
              element={guarded(
                <ErrorBoundary label="Observability">
                  <ObservabilityPage />
                </ErrorBoundary>,
              )}
            />
            <Route
              path="/outbound-mcp"
              element={guarded(
                <ErrorBoundary label="Outbound MCP">
                  <OutboundMcpPage />
                </ErrorBoundary>,
              )}
            />
            <Route
              path="/files"
              element={guarded(
                <ErrorBoundary label="Files">
                  <FilesPage />
                </ErrorBoundary>,
              )}
            />
            <Route
              path="/chat"
              element={guarded(
                <ErrorBoundary label="Chat">
                  <RunChatPage />
                </ErrorBoundary>,
              )}
            />
            <Route
              path="/builder"
              element={guarded(
                <ErrorBoundary label="Builder">
                  <BuilderPage currentUser={user} />
                </ErrorBoundary>,
              )}
            />
            <Route
              path="/simulation-studio"
              element={guarded(
                <ErrorBoundary label="Simulation Studio">
                  <AgentSimulationStudioPage />
                </ErrorBoundary>,
              )}
            />
            <Route
              path="/product-position"
              element={productWorkbenchGuarded(
                <ErrorBoundary label="Product Workbench">
                  <ProductPositionPage />
                </ErrorBoundary>,
              )}
            />
            <Route
              path="/product-position/:surfaceId"
              element={productWorkbenchGuarded(
                <ErrorBoundary label="Product Surface">
                  <ProductSurfacePage />
                </ErrorBoundary>,
              )}
            />
            <Route
              path="/evaluations"
              element={guarded(
                <ErrorBoundary label="Evaluation Lab">
                  <EvaluationLabPage />
                </ErrorBoundary>,
              )}
            />
            <Route
              path="/prompts"
              element={guarded(
                <ErrorBoundary label="Prompt Management">
                  <PromptManagementPage />
                </ErrorBoundary>,
              )}
            />
            <Route
              path="/business-roi"
              element={guarded(
                <ErrorBoundary label="Business ROI Cockpit">
                  <BusinessRoiCockpitPage />
                </ErrorBoundary>,
              )}
            />
            <Route
              path="/agent-releases"
              element={guarded(
                <ErrorBoundary label="Agent Release Management">
                  <AgentReleaseManagementPage />
                </ErrorBoundary>,
              )}
            />
            <Route
              path="/marketplace"
              element={guarded(
                <ErrorBoundary label="Marketplace">
                  <MarketplacePage />
                </ErrorBoundary>,
              )}
            />
            <Route
              path="/marketplace/catalog/:templateId"
              element={guarded(
                <ErrorBoundary label="Marketplace listing">
                  <MarketplaceCatalogDetailPage />
                </ErrorBoundary>,
              )}
            />
            <Route
              path="/marketplace/registry"
              element={guarded(
                <ErrorBoundary label="Pack registry">
                  <PackMarketplaceRegistryPage currentUser={user} />
                </ErrorBoundary>,
              )}
            />
            <Route
              path="/templates/:templateId"
              element={guarded(
                <ErrorBoundary label="Template wizard">
                  <TemplateWizardPage currentUser={user} />
                </ErrorBoundary>,
              )}
            />
            <Route
              path="/settings"
              element={guarded(
                <ErrorBoundary label="Settings">
                  <SettingsPage currentUser={user} section="general" />
                </ErrorBoundary>,
              )}
            />
            <Route
              path="/settings/users"
              element={guarded(
                <ErrorBoundary label="Settings users">
                  <SettingsPage currentUser={user} section="users" />
                </ErrorBoundary>,
              )}
            />
            <Route
              path="/settings/api"
              element={guarded(
                <ErrorBoundary label="Settings API keys">
                  <SettingsPage currentUser={user} section="api" />
                </ErrorBoundary>,
              )}
            />
            <Route
              path="/settings/runners"
              element={guarded(
                <ErrorBoundary label="Settings local runners">
                  <SettingsPage currentUser={user} section="runners" />
                </ErrorBoundary>,
              )}
            />
            <Route
              path="/settings/theme"
              element={guarded(
                <ErrorBoundary label="Settings theme">
                  <SettingsPage currentUser={user} section="theme" />
                </ErrorBoundary>,
              )}
            />
            <Route
              path="/settings/features"
              element={guarded(
                <ErrorBoundary label="Settings features">
                  <SettingsPage currentUser={user} section="flags" />
                </ErrorBoundary>,
              )}
            />
            <Route
              path="/settings/enterprise"
              element={guarded(
                <ErrorBoundary label="Settings enterprise">
                  <SettingsPage currentUser={user} section="enterprise" />
                </ErrorBoundary>,
              )}
            />
            <Route
              path="/admin/dlp"
              element={guarded(
                <ErrorBoundary label="DLP policy">
                  <AdminDlpPolicyPage />
                </ErrorBoundary>,
              )}
            />
            <Route
              path="/admin/dlp-activity"
              element={guarded(
                <ErrorBoundary label="DLP activity">
                  <AdminDlpActivityPage />
                </ErrorBoundary>,
              )}
            />
            <Route
              path="*"
              element={<Navigate to={needsOnboarding ? "/onboarding" : "/dashboard"} replace />}
            />
          </Routes>
        </Suspense>
      </main>
    </div>
  );
}
